import {
  AppraisalModuleEnum,
  DealClient,
  OwnershipRoleEnum,
  PostalAddress,
  ProductTypeEnum,
} from "@src/apis/ModularClient";
import { AddressHistoryDTO } from "@js/DTO/AddressHistoryDTO.cs";
import { EditorSettings } from "tinymce";

export class SharedDealService {
  tinymceOptions: EditorSettings = {
    content_style: "body {color: black;font:300 12px Roboto,sans-serif;}",
    menubar: false,
    height: 150,
    branding: false,
    statusbar: false,
    plugins: [
      "advlist autolink lists link image charmap print preview anchor",
      "searchreplace visualblocks code fullscreen",
      "insertdatetime media table paste code help wordcount",
    ].join(" "),
    toolbar:
      "undo redo | formatselect | " +
      "bold italic backcolor forecolor | alignleft aligncenter " +
      "alignright alignjustify | bullist numlist outdent indent | preview |" +
      "removeformat | help",
    external_plugins: undefined,
  };

  static $inject = ["$q"];

  constructor(private $q: ng.IQService) {}

  disablePersonalDetails(currentApplicant: DealClient, personalDetailsForm: ng.IFormController) {
    if (currentApplicant == null) return true;

    let selectFields = [currentApplicant.profile.countryOfResidence];

    if (personalDetailsForm.$invalid) return true;

    for (let i = 0; i < selectFields.length; i++) {
      if (selectFields[i] == "0" || selectFields[i] == undefined) {
        return true;
      }
    }

    return false;
  }

  modifyDateForFrontend(property: Date) {
    let receivedDate = new Date(property.getTime());

    receivedDate.setUTCMinutes(receivedDate.getUTCMinutes() + receivedDate.getTimezoneOffset());
    property = receivedDate;
    return property;
  }

  modifyDateForBackend(property: Date) {
    let originalDate = new Date(property.getTime());

    originalDate.setUTCMinutes(originalDate.getUTCMinutes() - originalDate.getTimezoneOffset());
    property = originalDate;
    return property;
  }

  /**
   * Determines when to show/hide the Invite button
   * @param shareholder
   */
  showInviteButton(shareholder: DealClient) {
    // If the shareholder has already been verified then don't show the button
    if (shareholder.isVerified) {
      return false;
    }

    // At least 2 of the security questions should be answered
    var count = 0;

    if (shareholder.client.phoneNumber) {
      count++;
    }

    if (shareholder.client.postalAddress.postCode) {
      count++;
    }

    if (shareholder.client.dateOfBirth) {
      count++;
    }

    if (count >= 2) {
      return true;
    }

    return false;
  }

  nonUkWarningVisible(currentApplicant: DealClient): boolean {
    if (currentApplicant && currentApplicant.profile && currentApplicant.profile.countryOfResidence) {
      return (
        currentApplicant.profile.countryOfResidence != "United Kingdom of Great Britain and Northern Ireland (the)"
      );
    }
    return false;
  }

  constructShareNoteMessage(): string {
    return "Please upload the supporting documents associated with your project.\n\nAdding supporting documents helps lenders make quicker and more reliable decisions.";
  }

  addressHistoryComplete(currentApplicant, applicantAddressHistory, missingAddressGaps) {
    const gapAllowed = 7 /* Number of days */ * 24 * 60 * 60 * 1000;
    const gaps = [];

    // Calculate the target date 3 years ago
    const threeYearsAgo = new Date();
    threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);

    const currentTime = currentApplicant.profile.currentAddressFrom;

    // Exit early if the current address already covers 3 years
    if (currentTime <= threeYearsAgo) {
      return true;
    }

    // Sort the applicant address history by FromDate in descending order
    applicantAddressHistory.sort((a, b) => b.fromDate.getTime() - a.fromDate.getTime());

    let lastToDate = currentTime; // Initialize lastToDate with the current address's FromDate

    // Loop through sorted history to identify gaps
    for (const { fromDate, toDate } of applicantAddressHistory) {
      // Register a gap if one exists between toDate and lastToDate
      if (toDate.getTime() + gapAllowed < lastToDate.getTime()) {
        gaps.push([new Date(toDate.getTime()), new Date(lastToDate.getTime())]);
      }

      lastToDate = fromDate;
    }

    // Check for a gap between the earliest FromDate and threeYearsAgo
    if (lastToDate > threeYearsAgo) {
      gaps.push([threeYearsAgo, lastToDate]);
    }

    // Convert the gaps into readable strings
    missingAddressGaps = gaps.map((gap) => `${gap[0].toLocaleDateString()} to ${gap[1].toLocaleDateString()}`);

    // Return false if gaps are present, otherwise return true
    return gaps.length === 0;
  }

  getNextAddressHistoryId(applicantAddressHistory: AddressHistoryDTO[]): number {
    if (applicantAddressHistory.length == 0) {
      return 1;
    } else {
      // Get the current maximum Id and add one to it for the next ID
      let id: number = Math.max.apply(
        Math,
        applicantAddressHistory.map(function (a) {
          return a.Id;
        }),
      );
      id++;
      return id;
    }
  }

  getAddressAsString(address: PostalAddress) {
    let addressAsString: string = "";

    addressAsString = this.concatenateAddressLine(address.addressLine1, addressAsString);
    addressAsString = this.concatenateAddressLine(address.addressLine2, addressAsString);
    addressAsString = this.concatenateAddressLine(address.addressLine3, addressAsString);
    addressAsString = this.concatenateAddressLine(address.addressLine4, addressAsString);

    return addressAsString;
  }

  concatenateAddressLine(addressLine: string, concatenatedAddress: string) {
    if (addressLine != null && addressLine.length > 0) {
      if (concatenatedAddress.length > 0) {
        concatenatedAddress = concatenatedAddress.concat(", ", addressLine);
      } else {
        concatenatedAddress = concatenatedAddress.concat(addressLine);
      }
    }

    return concatenatedAddress;
  }

  getOwnershipPercentageText(currentApplicant, productType) {
    if (productType == ProductTypeEnum.CommercialOwnerOccupied) {
      if (
        currentApplicant.OwnershipRole == OwnershipRoleEnum.Shareholder ||
        currentApplicant.OwnershipRole == OwnershipRoleEnum.DirectorAndShareholder
      ) {
        return "Shareholding";
      } else if (currentApplicant.OwnershipRole == OwnershipRoleEnum.Member) {
        return "Member";
      } else if (currentApplicant.OwnershipRole == OwnershipRoleEnum.Partner) {
        return "Partner";
      }
    } else {
      return "Shareholding";
    }
  }

  showBasedOnMainResidence(dealDto): boolean {
    return (
      dealDto.IsFamilyInResidence != 1 ||
      (dealDto.IsFamilyInResidence == 1 &&
        (dealDto.ProductType == ProductTypeEnum.BridgingPurchaseOrRefinance ||
          dealDto.ProductType == ProductTypeEnum.BridgingRefurb))
    );
  }

  downloadFile(fileUrl: string, fileName: string) {
    fetch(fileUrl)
      .then((response) => response.blob()) // Convert the response to a Blob
      .then((blob) => {
        // Create a temporary URL for the Blob
        const blobUrl = window.URL.createObjectURL(blob);

        // Create a hidden <a> element
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;

        // Append the link to the body and click it programmatically
        document.body.appendChild(link);
        link.click();

        // Clean up: Remove the link and revoke the Blob URL
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      });
  }

  getTime(date?: Date): number {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return 0; // Return 0 for invalid dates
    }
    return date.getTime();
  }

  /**
   * Tests whether a string is a date and converts it if so - used in JSON.parse
   * @param key
   * @param value
   */
  reviver(key, value) {
    if (typeof value === "string" && (key === "toDate" || key === "fromDate")) {
      const timestamp = new Date(value).getTime(); // this is a hack to fix native Date.Parse() being overwritten by datejs, which is returns null rather than NaN

      if (!isNaN(timestamp)) {
        return new Date(timestamp); // Only return a Date if timestamp is valid
      }

      return null; // Return `null` explicitly for invalid dates
    }

    return value; // Return the value unchanged for non-date keys
  }
}
